import React from 'react';
import './styles.scss';

interface InputTextV6Props {
    name: string;
    label?: string;
    type: string; // e.g., text, email, password, number
    placeholder?: string;
    value?: string;
    errorMessage?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    isRequired?: boolean;
    pattern?: string;
    isFocused?: boolean;
    isValid?: boolean;
    isDisabled?: boolean;
    className?: string;
    maxLength?: number;
    minValue?: number;
    hintText?: string;
    hintTextWhiteColor?: boolean;
}

interface InputTextV6State {
    formValidated: boolean;
}

class InputTextV6 extends React.Component<InputTextV6Props, InputTextV6State> {
    private containerRef = React.createRef<HTMLDivElement>();
    private inputRef = React.createRef<HTMLInputElement>();
    private observer?: MutationObserver;

    constructor(props: InputTextV6Props, context?: unknown) {
        super(props, context);
        this.state = {
            formValidated: false,
        };
    }

    componentDidMount() {
        this.attachFormValidationObserver();

        if (this.props.isFocused && this.inputRef.current) {
            this.inputRef.current.focus();
        }
    }

    componentWillUnmount() {
        this.removeFormValidationObserver();
    }

    /** Observe the form for class changes (e.g., Bootstrap `was-validated`) */
    private attachFormValidationObserver = () => {
        const form = this.containerRef.current?.closest('form');
        if (form) {
            this.observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    const target = mutation.target as HTMLElement;
                    if (target.classList.contains('was-validated')) {
                        this.setState({ formValidated: true });
                    }
                });
            });
            this.observer.observe(form, { attributes: true, attributeFilter: ['class'] });
        }
    };

    /** Remove observer when component unmounts */
    private removeFormValidationObserver = () => {
        if (this.observer) {
            this.observer.disconnect();
        }
    };

    private handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (this.props.onChange) {
            this.props.onChange(e);
        }
    };

    private handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        if (this.props.onFocus) {
            this.props.onFocus(e);
        }
    };

    private handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (this.props.onBlur) {
            this.props.onBlur(e);
        }
    };

    private handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (this.props.onKeyUp) {
            this.props.onKeyUp(e);
        }
    };

    private handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (this.props.onKeyDown) {
            this.props.onKeyDown(e);
        }
    };

    render() {
        const className = this.props.className || '';
        const maxLength = this.props.maxLength || 100;

        // Show error only after form is validated and if required field is empty or explicitly invalid
        const showError =
            this.state.formValidated &&
            ((!!this.props.isRequired && (!this.props.value || this.props.value.trim().length === 0)) || this.props.isValid === false);

        const invalidClass = showError ? 'is-invalid' : '';
        const inputClasses = `form-control ${invalidClass}`.trim();

        return (
            <div className={`${className || 'mb-3'}`} ref={this.containerRef}>
                <div className='input-wrapper'>
                    {this.props.label && (
                        <label htmlFor={this.props.name} className='input-label'>
                            {this.props.label}
                        </label>
                    )}
                    <input
                        className={inputClasses}
                        id={this.props.name}
                        name={this.props.name}
                        type={this.props.type}
                        placeholder={this.props.placeholder}
                        value={this.props.value || ''}
                        onChange={this.handleChange}
                        onFocus={this.handleFocus}
                        onBlur={this.handleBlur}
                        onKeyUp={this.handleKeyUp}
                        onKeyDown={this.handleKeyDown}
                        disabled={this.props.isDisabled}
                        ref={this.inputRef}
                        maxLength={maxLength}
                        autoComplete='off'
                        min={this.props.minValue}
                        required={this.props.isRequired}
                        pattern={this.props.pattern}
                    />
                    {showError && this.props.errorMessage && <div className='invalid-feedback'>{this.props.errorMessage}</div>}

                    {this.props.hintText && (
                        <div style={{ marginTop: '2px' }}>
                            <small className={`form-text sub-title ${this.props.hintTextWhiteColor ? 'text-light' : 'text-muted'}`}>
                                {this.props.hintText}
                            </small>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default InputTextV6;
